'use strict';

import shave from 'shave';

const { getDependencyClass } = require('../../lib/dependencies.js');
const performanceMeasure = require('../../lib/performanceMeasure.js');
let measure = performanceMeasure('webcomponents:ellipsis');

getDependencyClass('phoenix-page').then((pageClass) => {
    /**
     * <phoenix-ellipsis> Web Component.
     *
     * This is a wrapper around the Shave library:
     * https://github.com/dollarshaveclub/shave
     *
     * It applies the ellipsis on the element of the subtree marked with '.m-ellipsis--text'.
     * Additionally, it waits for the fonts (currently Typekit) to be loaded before running,
     * and watches for breakpoint changes to rerun.
     *
     * Known limitations:
     * - As the used library tries to use the native CSS ellipsis when possible, the text can
     *   be truncated in the middle of a word instead of at the first space before the word.
     * - Going from a small screen to a large screen (eg: breakpoint A to C) does not recover
     *   the previously truncated text.
     *
     * @example
     *   <phoenix-ellipsis class="m-ellipsis">
     *     <h2 class="m-ellipsis--text">text that will be truncated</h2>
     *   </phoenix-ellipsis>
     */
    class PhoenixEllipsis extends pageClass.PhoenixElement {
        get observedBroadcastedEvents() {
            return ['phoenix:breakpoint:changed'];
        }

        connectedCallback() {
            super.connectedCallback();
            const promises = [this.waitForElement('.m-ellipsis--text', { untilDomReady: true })];

            if (window.fontLoadedPromise) {
                // If this site uses custom fonts.
                promises.push(window.fontLoadedPromise);
            }

            const ellipsis = this._ellipsis.bind(this);

            Promise.all(promises).then((values) => {
                this.ellipsisText = values[0];
                this._getWaitForFontPromise().then(ellipsis);
            });

            if (!this.classList.contains('m-ellipsis')) {
                this.waitForElement('.m-ellipsis').then((el) => {
                    this.ellipsisElement = el;
                });
            } else {
                this.ellipsisElement = this;
            }
        }

        // Use the FontFaceSet.check browser API to wait until the font used by the element
        // is actually loaded
        _getWaitForFontPromise() {
            let fontPromiseResolve;
            let tries = 0;
            const checkFont = () => {
                try {
                    tries++;
                    if (document.fonts.check(getComputedStyle(this.ellipsisText).font)) {
                        fontPromiseResolve();
                    } else {
                        if (tries > 10) {
                            // Maybe the font just isn't available; we don't want to
                            // keep checking indefinitely
                            fontPromiseResolve();
                        } else {
                            setTimeout(checkFont, 200);
                        }
                    }
                } catch (e) {
                    // Likely because document.fonts.check is not available
                    // (under test or old browser), just proceed and do our best
                    // without necessarily having the right font
                    fontPromiseResolve();
                }
            };

            return new Promise((resolve) => {
                fontPromiseResolve = resolve;
                checkFont();
            });
        }

        _getHeight() {
            const { height, maxHeight } = window.getComputedStyle(this.ellipsisElement);
            let result = parseInt(maxHeight);
            if (isNaN(result)) {
                result = parseInt(height);
            }

            // In some cases the line-height can bleed out of the height of the element
            // creating a false positive for scrollHeight greater than the element height.
            const { lineHeight, fontSize } = window.getComputedStyle(this.ellipsisText);

            const fontLineHeightDiff = parseInt(lineHeight) - parseInt(fontSize);
            const heightBuffer = Math.max(fontLineHeightDiff, 1);

            // Adding heightBuffer to the height calculation to aviod the false scrollHeight check
            return Math.ceil(result + heightBuffer);
        }

        _ellipsis() {
            const scrollHeight = Math.ceil(this.ellipsisText.scrollHeight);
            const height = this._getHeight();
            if (!isNaN(height) && scrollHeight && scrollHeight > height) {
                shave(this.ellipsisText, height, { character: '...' });
            }
        }

        broadcastedEventCallback(name, args = {}) {
            if (name === 'phoenix:breakpoint:changed' && this.ellipsisText) {
                this._ellipsis();
            }
        }
    }
    window.customElements.define('phoenix-ellipsis', PhoenixEllipsis);
    measure.end();
});
